import { createSlice } from "@reduxjs/toolkit";
import {
  getCcpCredentials,
  getCcpInitializedStatus,
  getCcpAgent,
  getCcpContact,
  getCcpContactStatus,
  getCcpValidationButtonStatus,
  type CcpAgentQueue,
  type CcpAgentConfiguration,
  type CcpAgentPayload,
  type CcpContactPayload,
  type CcpContactStatusPayload,
} from "../services/ccp";

interface CcpAgentState {
  agentName: string | null;
  agentEmailId: string | null;
  agentStatus: string | null;
  agentRoutingName: string | null;
  agentRoutingLob: string | null;
  agentRoutingQueues: CcpAgentQueue[];
  endpoints: unknown[];
  queues: string[];
}

interface CcpContactState {
  contactId: string | null;
  contactAttributes: Record<string, { value: string }> | null;
  contactData: unknown | null;
}

interface CcpState {
  status: string;
  ccpCredentials: Record<string, unknown>;
  isCcpInitialized: boolean;
  ccpAgent: CcpAgentState;
  ccpContact: CcpContactState;
  ccpContactStatus: string | null;
  ccpContactMethod: string | null;
  ccpValidationButtonStatus: boolean;
}

const initialState: CcpState = {
  status: "idle",
  ccpCredentials: {},
  isCcpInitialized: false,
  ccpAgent: {
    agentName: null,
    agentEmailId: null,
    agentStatus: null,
    agentRoutingName: null,
    agentRoutingLob: null,
    agentRoutingQueues: [],
    endpoints: [],
    queues: [],
  },
  ccpContact: {
    contactId: null,
    contactAttributes: null,
    contactData: null,
  },
  ccpContactStatus: null,
  ccpContactMethod: null,
  ccpValidationButtonStatus: false,
};

const ccpSlice = createSlice({
  name: "ccp",
  initialState,
  reducers: {
    clearCCPContact: (state) => {
      state.ccpContact = {
        contactId: null,
        contactAttributes: null,
        contactData: null,
      };
    },
    clearCcpCredentials: (state) => {
      state.ccpCredentials = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCcpCredentials.pending, (state) => {
        state.status = "pending";
      })
      .addCase(getCcpCredentials.fulfilled, (state, action) => {
        const { payload: { data: { connectFederationTokens = {} } } = {} } =
          action;
        state.status = "success";
        state.ccpCredentials = connectFederationTokens;
      })
      .addCase(getCcpCredentials.rejected, (state) => {
        state.status = "rejected";
      })
      .addCase(getCcpInitializedStatus, (state, action) => {
        state.isCcpInitialized = action.payload as unknown as boolean;
      })
      .addCase(getCcpAgent, (state, action) => {
        const {
          agentStatus,
          configuration: { name, username, routingProfile },
          endpoints,
        } = action.payload as unknown as {
          agentStatus: string;
          configuration: CcpAgentConfiguration;
          endpoints: unknown[];
        };
        state.ccpAgent.agentName = name;
        state.ccpAgent.agentEmailId = username;
        state.ccpAgent.agentStatus = agentStatus;
        state.ccpAgent.agentRoutingName = routingProfile.name;
        state.ccpAgent.agentRoutingQueues = routingProfile.queues;
        state.ccpAgent.agentRoutingLob =
          routingProfile.name && routingProfile.name.split("-")[0];
        state.ccpAgent.endpoints = endpoints;
        state.ccpAgent.queues = routingProfile.queues.reduce<string[]>(
          (acc, cur: CcpAgentQueue) => {
            cur.name && acc.push(cur.name);
            return acc;
          },
          [],
        );
      })
      .addCase(getCcpContact, (state, action) => {
        const { contactId, contactAttributes, contactData } =
          action.payload as unknown as CcpContactPayload;
        if (!!contactId) {
          state.ccpContact.contactId = contactId;
        }
        if (!!contactAttributes) {
          state.ccpContact.contactAttributes = contactAttributes;
        }
        if (!!contactData) {
          state.ccpContact.contactData = contactData;
        }
      })
      .addCase(getCcpContactStatus, (state, action) => {
        const payload = action.payload as
          | CcpContactStatusPayload
          | null
          | undefined;
        if (!!payload) {
          const { status, method } = payload;
          state.ccpContactStatus = status;
          // Only update value when it's available
          if (!!method) {
            state.ccpContactMethod = method;
          }
        } else {
          state.ccpContactStatus = payload ?? null;
          state.ccpContactMethod = payload ?? null;
        }
      })
      .addCase(getCcpValidationButtonStatus, (state, action) => {
        state.ccpValidationButtonStatus = action.payload as unknown as boolean;
      });
  },
});
export const { clearCCPContact, clearCcpCredentials } = ccpSlice.actions;
export default ccpSlice.reducer;
